import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography } from '@mui/material';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import ContentArea from "../components/content-area/Content";
import CharitiesPng from "../assets/icons/charities.png";
import CharityCardList from "../components/charity/CharityCardList";
import FinanceSpaceSelector from "../components/finance/FinanceSpaceSelector";
import { getCharities, getCharitiesForSpace } from "../api/charityApi";
import { useSpaceId } from "../hooks/useSpaceId";
import { usePermissions } from "../hooks/usePermissions";
import { useSessionState } from "../hooks/useSessionState";
import { signalRService } from "../services/signalRService";
import { SIGNALR_EVENTS } from '../constants/signalREvents';
import { CONTENT_TYPES } from '../constants/contentTypes';

const Charities = () => {
  const spaceId = useSpaceId();
  const { isFinanceMode } = usePermissions();
  const [charities, setCharities] = useState([]);
  const [selectedSpace, setSelectedSpace] = useSessionState('tifoxr_finance_space_charities', null);

  // ── Normal space mode ───────────────────────────────────────────────────────
  const loadCharities = useCallback(async () => {
    try {
      const data = await getCharities();
      setCharities(data);
    } catch (err) {
      console.error('[load error]', err);
    }
  }, []);

  useEffect(() => {
    if (isFinanceMode || !spaceId) return;
    loadCharities();
  }, [spaceId, isFinanceMode, loadCharities]);

  useEffect(() => {
    if (isFinanceMode) return;
    const handle = (payload) => {
      if (payload?.contentType === CONTENT_TYPES.CHARITY) loadCharities();
    };
    signalRService.on(SIGNALR_EVENTS.APPROVAL_DECISION, handle);
    return () => signalRService.off(SIGNALR_EVENTS.APPROVAL_DECISION, handle);
  }, [loadCharities, isFinanceMode]);

  useEffect(() => {
    if (isFinanceMode) return;
    const handle = (payload) => {
      if (payload?.contentType === CONTENT_TYPES.CHARITY) loadCharities();
    };
    signalRService.on(SIGNALR_EVENTS.APPROVAL_WITHDRAWN, handle);
    return () => signalRService.off(SIGNALR_EVENTS.APPROVAL_WITHDRAWN, handle);
  }, [loadCharities, isFinanceMode]);

  // ── Finance Portal space change ─────────────────────────────────────────────
  const handleSpaceSelect = (space) => {
    setSelectedSpace(space);
    setCharities([]);
  };

  return (
    <ContentArea title="Charities" icon={CharitiesPng}>
      {isFinanceMode && (
        <FinanceSpaceSelector
          selectedSpaceId={selectedSpace?.spaceId ?? null}
          onSpaceSelect={handleSpaceSelect}
        />
      )}

      {isFinanceMode ? (
        selectedSpace ? (
          <CharityCardList
            key={selectedSpace.spaceId}
            charities={charities}
            setCharities={setCharities}
            onFetch={() => getCharitiesForSpace(selectedSpace.spaceId)}
            overrideSpaceId={selectedSpace.spaceId}
          />
        ) : (
          <Box sx={{ textAlign: 'center', py: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <AccountBalanceOutlinedIcon sx={{ fontSize: 56, color: '#a7f3d0' }} />
            <Typography variant="body1" sx={{ color: '#94a3b8', fontWeight: 500 }}>
              Select a space above to view charities
            </Typography>
          </Box>
        )
      ) : (
        <CharityCardList charities={charities} setCharities={setCharities} />
      )}
    </ContentArea>
  );
};

export default Charities;
